/**
 * useConfiguredOrders Hook
 *
 * Hook customizado que utiliza as configurações do localStorage para fazer requests
 * para o endpoint /orders/today com os parâmetros corretos
 */

import { useTodayOrders } from '@queries/order';
import { wsLog } from '@utils/wsLogger';
import { useFilaConfig } from './useFilaConfig';
import { configToApiParams, validateConfigForRequest } from '../utils/config-to-api-params';
import type { TodayOrdersParams } from '@services/api/orderTypes';

interface UseConfiguredOrdersParams {
  currentIntervalId?: string;
  enabled?: boolean;
  staleTime?: number;
}

/**
 * Hook que faz requests para /orders/today usando configurações do localStorage
 */
export function useConfiguredOrders({
  currentIntervalId,
  enabled = true,
  staleTime = 5 * 60 * 1000, // 5 minutos default
}: UseConfiguredOrdersParams = {}) {
  const { config } = useFilaConfig();
  // alert(JSON.stringify(config));

  // Converter configurações para parâmetros da API
  const params = configToApiParams(config, currentIntervalId);

  // Validar configurações
  const validation = validateConfigForRequest(config);
  const isEnabled = enabled && validation.isValid && !!config.establishmentId;

  // Log para debugging
  wsLog.debug('🔧 useConfiguredOrders Hook:', {
    config: {
      orderType: config.orderType,
      autoIntervalChange: config.autoIntervalChange,
      selectedInterval: config.selectedInterval,
      establishmentId: config.establishmentId,
    },
    currentIntervalId,
    params,
    validation,
    isEnabled,
    timestamp: new Date().toISOString(),
  });

  // Usar o hook useTodayOrders existente com os parâmetros convertidos
  const result = useTodayOrders(params, {
    enabled: isEnabled,
    staleTime,
  });

  // Adicionar informação de validação ao resultado
  return {
    ...result,
    params,
    validation,
    isConfigValid: validation.isValid,
  };
}

/**
 * Hook específico para pedidos da fila digital
 */
export function useFilaOrders(currentIntervalId?: string) {
  const { config } = useFilaConfig();

  // OrderType é 'queue' | 'lunchbox'. A comparação era contra 'fila', valor
  // que a configuração nunca armazena — o hook ficava sempre desabilitado.
  const enabled = config.orderType === 'queue';

  return useConfiguredOrders({
    currentIntervalId,
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutos para fila (mais crítico)
  });
}

/**
 * Hook específico para pedidos da lancheira digital
 */
export function useLancheiraOrders(currentIntervalId?: string) {
  const { config } = useFilaConfig();

  // Mesma correção: o valor gravado é 'lunchbox', não 'lancheira'.
  const enabled = config.orderType === 'lunchbox';

  return useConfiguredOrders({
    currentIntervalId,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos para lancheira
  });
}

/**
 * Hook que retorna os pedidos baseados no tipo configurado
 * Este é o hook principal que deve ser usado no componente
 */
export function useOrdersByConfigType(currentIntervalId?: string) {
  const { config } = useFilaConfig();

  wsLog.debug('🔧 useOrdersByConfigType Hook:', {
    orderType: config.orderType,
    currentIntervalId,
    establishmentId: config.establishmentId,
  });

  // Usar o hook genérico que já aplica as configurações
  return useConfiguredOrders({
    currentIntervalId,
    enabled: true, // Sempre habilitado, a validação interna cuida do resto
  });
}
